import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const http = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
});

// 请求拦截：附加 access token
http.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截：401 自动刷新
let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

http.interceptors.response.use(
  (resp) => resp.data, // 直接返回 data
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !original._retry) {
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) {
        useAuthStore.getState().logout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // 排队等待刷新结果
        return new Promise((resolve, reject) => {
          pendingQueue.push((token) => {
            if (!token) return reject(error);
            original._retry = true;
            original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
            resolve(http.request(original));
          });
        });
      }

      isRefreshing = true;
      try {
        const resp = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
        const newTokens = resp.data;
        useAuthStore.getState().setTokens(newTokens.accessToken, newTokens.refreshToken);
        pendingQueue.forEach((cb) => cb(newTokens.accessToken));
        pendingQueue = [];
        original._retry = true;
        original.headers = { ...original.headers, Authorization: `Bearer ${newTokens.accessToken}` };
        return http.request(original);
      } catch (e) {
        pendingQueue.forEach((cb) => cb(null));
        pendingQueue = [];
        useAuthStore.getState().logout();
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
